'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FEATURE_FLAGS } from '@/feature-flags/consts/flags.const';
import { evaluateVariantFlagClient } from '@/feature-flags/client/variant-evaluator';
import { useFeatureFlagProvider } from '@/feature-flags/client/provider';
import type {
  LevelTwoConfig,
  UseLevelTwoResult,
} from '@/feature-flags/types/variant.types';
import { DISABLED_LEVEL_TWO_CONFIG } from '@/feature-flags/types/variant.types';
import { parseLevelTwoConfig } from '@/feature-flags/utils/attachment-parser';
import { levelTwoEmitter } from '@/feature-flags/utils/level-two-emitter';
import { logInfo, logWarning, logError } from '@/feature-flags/utils/logger';
import { setSessionId, getCorrelationContext, generateEvaluationId, clearSessionId } from '@/feature-flags/utils/correlation';

export function useOperationalLevelTwo(userId?: string): UseLevelTwoResult {
  const { isReady, error: providerError } = useFeatureFlagProvider();
  const [config, setConfig] = useState<LevelTwoConfig>(DISABLED_LEVEL_TWO_CONFIG);
  const [variant, setVariant] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [isFallback, setIsFallback] = useState<boolean>(false);
  const [currentUserId, setCurrentUserId] = useState<string | undefined>(userId);
  const hasEvaluatedRef = useRef(false);
  const hasSuccessfulLoadRef = useRef(false);
  const hasLoadedOnceRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (currentUserId !== userId) {
      setCurrentUserId(userId);
      hasEvaluatedRef.current = false;
      hasSuccessfulLoadRef.current = false;
    }
  }, [userId, currentUserId]);

  const evaluate = useCallback(async () => {
    generateEvaluationId();

    if (userId) {
      setSessionId(userId);
    } else {
      clearSessionId();
    }
    const evaluationStartTime = performance.now();

    if (!hasLoadedOnceRef.current) {
      setLoading(true);
    }
    setError(null);

    try {
      logInfo('Evaluating Level Two rollout', {
        ...getCorrelationContext(),
        flagKey: FEATURE_FLAGS.OPERATIONAL_MEDPREV_WEB_APP_LEVEL_TWO,
        userId,
      });

      const result = await evaluateVariantFlagClient<string>(
        FEATURE_FLAGS.OPERATIONAL_MEDPREV_WEB_APP_LEVEL_TWO,
        userId,
      );

      if (!result.match) {
        const totalDuration = performance.now() - evaluationStartTime;

        logWarning('Level Two no-match, using fallback config', {
          ...getCorrelationContext(),
          flagKey: FEATURE_FLAGS.OPERATIONAL_MEDPREV_WEB_APP_LEVEL_TWO,
          userId,
          variant: result.variant,
          reason: 'no-match-fallback',
          totalDurationMs: totalDuration,
        });

        setConfig(DISABLED_LEVEL_TWO_CONFIG);
        setVariant('no-match-fallback');
        setIsFallback(true);
        hasSuccessfulLoadRef.current = true;
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        return;
      }

      setVariant(result.variant);
      setIsFallback(result.isFallback);

      const parsedConfig = parseLevelTwoConfig(result.attachment);
      const totalDuration = performance.now() - evaluationStartTime;

      logInfo('Level Two config loaded', {
        ...getCorrelationContext(),
        flagKey: FEATURE_FLAGS.OPERATIONAL_MEDPREV_WEB_APP_LEVEL_TWO,
        userId,
        variant: result.variant,
        config: parsedConfig,
        isFallback: result.isFallback,
        totalDurationMs: totalDuration,
      });

      setConfig({ ...parsedConfig });

      if (result.variant) {
        hasSuccessfulLoadRef.current = true;
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }
    } catch (error) {
      const evalError = error instanceof Error ? error : new Error(String(error));
      const totalDuration = performance.now() - evaluationStartTime;

      logError('Level Two evaluation failed', error, {
        ...getCorrelationContext(),
        flagKey: FEATURE_FLAGS.OPERATIONAL_MEDPREV_WEB_APP_LEVEL_TWO,
        userId,
        totalDurationMs: totalDuration,
      });

      setError(evalError);
      setConfig(DISABLED_LEVEL_TWO_CONFIG);
      setVariant(null);
      setIsFallback(true);
    } finally {
      setLoading(false);
      hasLoadedOnceRef.current = true;
    }
  }, [userId]);

  useEffect(() => {
    if (!isReady) {
      setConfig({ ...DISABLED_LEVEL_TWO_CONFIG });
      setVariant(null);
      setLoading(true);
      setIsFallback(true);
      return;
    }

    if (providerError) {
      setError(providerError);
      setConfig(DISABLED_LEVEL_TWO_CONFIG);
      setVariant(null);
      setLoading(false);
      setIsFallback(true);
      hasEvaluatedRef.current = true;
      return;
    }

    if (!hasEvaluatedRef.current) {
      hasEvaluatedRef.current = true;
      evaluate();
    }
  }, [isReady, providerError, evaluate, userId]);

  useEffect(() => {
    if (!loading || hasSuccessfulLoadRef.current) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      if (levelTwoEmitter.hasLoaded() || hasSuccessfulLoadRef.current) {
        timeoutRef.current = null;
        return;
      }

      setLoading((currentLoading) => {
        if (!currentLoading) {
          return currentLoading;
        }

        logWarning('Level Two evaluation timeout', {
          ...getCorrelationContext(),
          flagKey: FEATURE_FLAGS.OPERATIONAL_MEDPREV_WEB_APP_LEVEL_TWO,
          userId,
          timeoutMs: 10000,
          reason: 'evaluation_timeout',
        });

        setConfig(DISABLED_LEVEL_TWO_CONFIG);
        setVariant(null);
        setIsFallback(true);
        setError(new Error('Evaluation timeout'));
        return false;
      });

      timeoutRef.current = null;
    }, 10000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const result = useMemo(
    () => ({
      config,
      loading,
      error,
      isFallback,
      refetch: evaluate,
      variant,
    } as UseLevelTwoResult),
    [config, loading, error, isFallback, evaluate, variant],
  );

  useEffect(() => {
    setTimeout(() => levelTwoEmitter.emit(result), 0);
  }, [result]);

  return result;
}
